/**
 * Hook binding the favorite-contact store to the active authenticated user.
 *
 * Rehydrates the favorite from localStorage whenever the signed-in user id
 * changes, and exposes the favorite resolved against the ACTIVE family's
 * Supabase members plus mutators. Stored ids are profile UUIDs; a stale id
 * (e.g. member left the family) resolves to null and self-heals on the next
 * star.
 *
 * @module features/p2p-chat/hooks/useFavoriteContact
 */

import { useEffect } from 'react';
import { useAuthStore } from '@/features/auth/stores/authStore';
import { useFamilyStore } from '@/features/family/stores/familyStore';
import type { FamilyMember } from '@/features/family/types/family.types';
import { useFavoriteContactStore } from '../stores/favoriteContactStore';

export interface UseFavoriteContactResult {
  /** Resolved favorite family member, or null when none/unknown. */
  readonly favorite: FamilyMember | null;
  /** Profile UUID of the favorite, or null. */
  readonly favoriteId: string | null;
  /** Set or toggle the favorite contact. */
  readonly setFavorite: (contactId: string) => void;
  /** Clear the favorite contact. */
  readonly clearFavorite: () => void;
}

export function useFavoriteContact(): UseFavoriteContactResult {
  const userId = useAuthStore((state) => state.user?.id ?? null);
  const members = useFamilyStore((state) => state.members);
  const favoriteId = useFavoriteContactStore((state) => state.favoriteId);
  const rehydrate = useFavoriteContactStore((state) => state.rehydrate);
  const setFavorite = useFavoriteContactStore((state) => state.setFavorite);
  const clearFavorite = useFavoriteContactStore((state) => state.clearFavorite);

  useEffect(() => {
    rehydrate(userId);
  }, [userId, rehydrate]);

  const favorite = favoriteId
    ? (members.find((m) => m.profileId === favoriteId) ?? null)
    : null;

  return { favorite, favoriteId, setFavorite, clearFavorite };
}
